/**
 * German Tax Calculations for Real Estate
 * Implements German tax law (EStG - Einkommensteuergesetz)
 */

import { PropertyInputs } from '../../types';

interface TaxSettings {
  marginalTaxRate: number;
  churchTax: boolean;
  solidarityTax: boolean;
  married: boolean;
  isDenkmal: boolean;
  hasEnergyRenovation: boolean;
  useSonderAfA: boolean;
  landValuePercentage: number;
}

interface TaxOptimizationResult {
  currentTaxBurden: number;
  optimizedTaxBurden: number;
  savings: number;
  savingsPercent: number;
  strategies: TaxStrategy[];
  afaDetails: AfADetails;
  speculationPeriod: SpeculationPeriodInfo;
  threeObjectRule: ThreeObjectRuleStatus;
}

interface TaxStrategy {
  id: string;
  title: string;
  description: string;
  potentialSaving: number;
  difficulty: 'easy' | 'medium' | 'hard';
  legalParagraph: string;
  implemented: boolean;
  requirements: string[];
}

interface AfADetails {
  regularAfA: number;
  denkmalAfA: number;
  energyAfA: number;
  sonderAfA: number;
  totalAfA: number;
  buildingValue: number;
  landValue: number;
  afaRate: number;
}

interface SpeculationPeriodInfo {
  purchaseDate: Date;
  speculationEndDate: Date;
  daysRemaining: number;
  taxFreeAfter: boolean;
  estimatedCapitalGains: number;
  capitalGainsTax: number;
}

interface ThreeObjectRuleStatus {
  salesInLast5Years: number;
  remainingBeforeCommercial: number;
  isCommercialTrading: boolean;
  taxImpact: string;
}

/**
 * Calculate German taxes and optimization strategies
 */
export function calculateGermanTaxes(
  property: PropertyInputs,
  settings: TaxSettings,
  _taxYear: number
): TaxOptimizationResult {
  
  // Calculate AfA details
  const afaDetails = calculateAfA(property, settings);
  
  // Calculate speculation period
  const speculationPeriod = calculateSpeculationPeriod(property, settings);
  
  // Check three object rule
  const threeObjectRule = checkThreeObjectRule(property);
  
  // Generate tax strategies
  const strategies = generateTaxStrategies(property, settings, afaDetails);
  
  // Calculate tax burden
  const currentTaxBurden = calculateCurrentTaxBurden(property, settings, afaDetails);
  const optimizedTaxBurden = calculateOptimizedTaxBurden(property, settings, afaDetails, strategies);
  
  return {
    currentTaxBurden,
    optimizedTaxBurden,
    savings: currentTaxBurden - optimizedTaxBurden,
    savingsPercent: ((currentTaxBurden - optimizedTaxBurden) / currentTaxBurden) * 100,
    strategies,
    afaDetails,
    speculationPeriod,
    threeObjectRule
  };
}

/**
 * Calculate AfA (Absetzung für Abnutzung) - Depreciation
 */
function calculateAfA(property: PropertyInputs, settings: TaxSettings): AfADetails {
  const purchasePrice = property.angebotspreis || 0;
  const landValue = purchasePrice * (settings.landValuePercentage / 100);
  const buildingValue = purchasePrice - landValue;
  
  // Determine AfA rate based on building age
  let afaRate: number;
  // Building age is used for AfA determination but variable unused warning
  
  if ((property.baujahr || 1990) < 1925) {
    afaRate = 2.5; // 2.5% for buildings before 1925 (40 years)
  } else {
    afaRate = 2.0; // 2% for buildings after 1924 (50 years)
  }
  
  // Regular AfA (§7 Abs. 4 EStG)
  const regularAfA = buildingValue * (afaRate / 100);
  
  // Denkmal AfA (§7i EStG) - 9% for 12 years on renovation costs
  let denkmalAfA = 0;
  if (settings.isDenkmal && property.anfangsinvestitionen) {
    // First 8 years: 9%, next 4 years: 7%
    denkmalAfA = property.anfangsinvestitionen * 0.09;
  }
  
  // Energy renovation (§35c EStG) - 20% over 3 years
  let energyAfA = 0;
  if (settings.hasEnergyRenovation && property.anfangsinvestitionen) {
    energyAfA = property.anfangsinvestitionen * 0.20 / 3;
  }
  
  // Sonder-AfA (§7b EStG) - Special depreciation for new rental housing
  let sonderAfA = 0;
  if (settings.useSonderAfA && property.baujahr >= 2023) {
    // 5% in first year, 5% in second year, 6% in third and fourth year
    sonderAfA = buildingValue * 0.05; // First year rate
  }
  
  const totalAfA = regularAfA + denkmalAfA + energyAfA + sonderAfA;
  
  return {
    regularAfA,
    denkmalAfA,
    energyAfA,
    sonderAfA,
    totalAfA,
    buildingValue,
    landValue,
    afaRate
  };
}

/**
 * Calculate speculation period (§23 EStG)
 */
function calculateSpeculationPeriod(property: PropertyInputs, settings: TaxSettings): SpeculationPeriodInfo {
  // Assume purchase date is 2 years ago if not specified
  const purchaseDate = new Date();
  purchaseDate.setFullYear(purchaseDate.getFullYear() - 2);
  
  const speculationEndDate = new Date(purchaseDate);
  speculationEndDate.setFullYear(speculationEndDate.getFullYear() + 10);
  
  const today = new Date();
  const daysRemaining = Math.max(0, Math.floor((speculationEndDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  
  // Calculate estimated capital gains
  const purchasePrice = property.angebotspreis || 0;
  const appreciationRate = (property.wertzuwachsProJahr || 3) / 100;
  const yearsHeld = (today.getFullYear() - purchaseDate.getFullYear());
  const currentValue = purchasePrice * Math.pow(1 + appreciationRate, yearsHeld);
  const capitalGains = currentValue - purchasePrice;
  
  // Calculate tax on capital gains
  const capitalGainsTax = daysRemaining > 0 ? capitalGains * (settings.marginalTaxRate / 100) : 0;
  
  return {
    purchaseDate,
    speculationEndDate,
    daysRemaining,
    taxFreeAfter: daysRemaining === 0,
    estimatedCapitalGains: capitalGains,
    capitalGainsTax
  };
}

/**
 * Check three object rule for commercial trading
 */
function checkThreeObjectRule(_property: PropertyInputs): ThreeObjectRuleStatus {
  // This would need to track actual sales history
  // For demo purposes, we'll use placeholder values
  const salesInLast5Years = 1; // Assume 1 sale
  const maxPrivateSales = 3;
  const remainingBeforeCommercial = Math.max(0, maxPrivateSales - salesInLast5Years);
  
  return {
    salesInLast5Years,
    remainingBeforeCommercial,
    isCommercialTrading: salesInLast5Years > maxPrivateSales,
    taxImpact: salesInLast5Years > maxPrivateSales ? 'Gewerblich' : 'Privat'
  };
}

/**
 * Generate tax optimization strategies
 */
function generateTaxStrategies(
  property: PropertyInputs,
  settings: TaxSettings,
  afaDetails: AfADetails
): TaxStrategy[] {
  const strategies: TaxStrategy[] = [];
  
  // Strategy 1: Optimize land/building split
  if (settings.landValuePercentage > 20) {
    strategies.push({
      id: 'optimize-split',
      title: 'Gebäude-Grund-Aufteilung optimieren',
      description: 'Reduzieren Sie den Grundstücksanteil auf 20% für höhere AfA',
      potentialSaving: (afaDetails.buildingValue * 0.1 * (afaDetails.afaRate / 100)) * (settings.marginalTaxRate / 100),
      difficulty: 'easy',
      legalParagraph: 'BMF-Schreiben zur Kaufpreisaufteilung',
      implemented: false,
      requirements: ['Gutachten zur Wertaufteilung', 'Abstimmung mit Finanzamt']
    });
  }
  
  // Strategy 2: Denkmalschutz-AfA
  if (!settings.isDenkmal && property.baujahr < 1950) {
    strategies.push({
      id: 'denkmal-afa',
      title: 'Denkmalschutz-AfA prüfen',
      description: '9% AfA für 12 Jahre auf Sanierungskosten',
      potentialSaving: (property.anfangsinvestitionen || 100000) * 0.09 * (settings.marginalTaxRate / 100),
      difficulty: 'hard',
      legalParagraph: '§7i EStG',
      implemented: settings.isDenkmal,
      requirements: ['Denkmalschutz-Bescheinigung', 'Abstimmung mit Denkmalbehörde']
    });
  }
  
  // Strategy 3: Energy renovation
  if (!settings.hasEnergyRenovation) {
    strategies.push({
      id: 'energy-renovation',
      title: 'Energetische Sanierung',
      description: '20% der Kosten über 3 Jahre steuerlich absetzen',
      potentialSaving: 40000 * 0.20 * (settings.marginalTaxRate / 100) / 3,
      difficulty: 'medium',
      legalParagraph: '§35c EStG',
      implemented: settings.hasEnergyRenovation,
      requirements: ['Fachunternehmer-Bescheinigung', 'Energieberater-Bestätigung']
    });
  }
  
  // Strategy 4: Disagio optimization
  strategies.push({
    id: 'disagio',
    title: 'Disagio bei Finanzierung',
    description: 'Vorauszahlung von Zinsen als sofortiger Werbungskosten-Abzug',
    potentialSaving: (property.angebotspreis * 0.02) * (settings.marginalTaxRate / 100),
    difficulty: 'easy',
    legalParagraph: '§11 EStG',
    implemented: false,
    requirements: ['Kreditvertrag mit Disagio', 'Max. 5% marktüblich']
  });
  
  // Strategy 5: Instandhaltungsrücklage
  strategies.push({
    id: 'maintenance-reserve',
    title: 'Erhaltungsaufwand bündeln',
    description: 'Instandhaltung im ersten Jahr als sofortiger Aufwand',
    potentialSaving: (property.wohnflaeche * 15) * (settings.marginalTaxRate / 100),
    difficulty: 'medium',
    legalParagraph: '§6 Abs. 1 Nr. 1a EStG',
    implemented: false,
    requirements: ['Innerhalb von 3 Jahren nach Kauf', 'Max. 15% des Gebäudewerts']
  });
  
  // Strategy 6: Vermietung an Angehörige
  if (settings.marginalTaxRate > 35) {
    strategies.push({
      id: 'family-rental',
      title: 'Vermietung an Angehörige',
      description: 'Verbilligte Vermietung (66% ortsüblich) mit vollem Werbungskostenabzug',
      potentialSaving: (property.kaltmieteProQm * property.wohnflaeche * 12 * 0.34) * (settings.marginalTaxRate / 100),
      difficulty: 'medium',
      legalParagraph: '§21 Abs. 2 EStG',
      implemented: false,
      requirements: ['Mindestens 66% der ortsüblichen Miete', 'Fremdvergleich beachten']
    });
  }
  
  return strategies;
}

/**
 * Calculate current tax burden
 */
function calculateCurrentTaxBurden(
  property: PropertyInputs,
  settings: TaxSettings,
  afaDetails: AfADetails
): number {
  const annualRent = (property.kaltmieteProQm * property.wohnflaeche + (property.stellplatzMiete || 0)) * 12;
  
  // Deductible expenses
  const hausgeld = (property.hausgeldNichtUmlagefaehig || 0) * 12;
  const maintenance = property.wohnflaeche * (property.instandhaltung || 10);
  const managementCost = (property.verwaltungskosten || 0) * 12;
  const interestExpense = property.angebotspreis * (property.finanzierungsanteil / 100) * (property.zinssatz / 100);
  const depreciation = afaDetails.regularAfA;
  
  const totalExpenses = hausgeld + maintenance + managementCost + interestExpense + depreciation;
  const taxableIncome = Math.max(0, annualRent - totalExpenses);
  
  let tax = taxableIncome * (settings.marginalTaxRate / 100);
  
  // Add church tax if applicable
  if (settings.churchTax) {
    tax *= 1.09; // 9% church tax on income tax
  }
  
  // Add solidarity surcharge if applicable
  if (settings.solidarityTax) {
    tax *= 1.055; // 5.5% solidarity surcharge
  }
  
  return tax;
}

/**
 * Calculate optimized tax burden
 */
function calculateOptimizedTaxBurden(
  property: PropertyInputs,
  settings: TaxSettings,
  afaDetails: AfADetails,
  strategies: TaxStrategy[]
): number {
  let currentTax = calculateCurrentTaxBurden(property, settings, afaDetails);
  
  // Apply savings from all implementable strategies
  const totalSavings = strategies
    .filter(s => !s.implemented)
    .reduce((sum, s) => sum + s.potentialSaving, 0);
  
  return Math.max(0, currentTax - totalSavings);
}

/**
 * German income tax calculation (progressive tax rate)
 */
export function calculateGermanIncomeTax(taxableIncome: number, married: boolean = false): number {
  // 2024 tax brackets
  let income = married ? taxableIncome / 2 : taxableIncome;
  let tax = 0;
  
  if (income <= 11604) {
    tax = 0; // Basic allowance
  } else if (income <= 17005) {
    const y = (income - 11604) / 10000;
    tax = (922.98 * y + 1400) * y;
  } else if (income <= 66760) {
    const z = (income - 17005) / 10000;
    tax = (181.19 * z + 2397) * z + 1025.38;
  } else if (income <= 277825) {
    tax = 0.42 * income - 10602.13;
  } else {
    tax = 0.45 * income - 18936.88;
  }
  
  return married ? tax * 2 : tax;
}

/**
 * Calculate Grunderwerbsteuer (real estate transfer tax) by state
 */
export function calculateGrunderwerbsteuer(purchasePrice: number, state: string = 'Berlin'): number {
  const taxRates: { [key: string]: number } = {
    'Bayern': 3.5,
    'Sachsen': 3.5,
    'Baden-Württemberg': 5.0,
    'Berlin': 6.0,
    'Brandenburg': 6.5,
    'Bremen': 5.0,
    'Hamburg': 4.5,
    'Hessen': 6.0,
    'Mecklenburg-Vorpommern': 6.0,
    'Niedersachsen': 5.0,
    'Nordrhein-Westfalen': 6.5,
    'Rheinland-Pfalz': 5.0,
    'Saarland': 6.5,
    'Sachsen-Anhalt': 5.0,
    'Schleswig-Holstein': 6.5,
    'Thüringen': 6.5
  };
  
  const rate = taxRates[state] || 6.0;
  return purchasePrice * (rate / 100);
}

/**
 * Calculate notary and land registry costs
 */
export function calculateNotaryAndRegistryCosts(purchasePrice: number): {
  notaryCost: number;
  registryCost: number;
  total: number;
} {
  // Notary costs approximately 1.0-1.5% of purchase price
  const notaryCost = purchasePrice * 0.015;
  
  // Land registry costs approximately 0.5% of purchase price
  const registryCost = purchasePrice * 0.005;
  
  return {
    notaryCost,
    registryCost,
    total: notaryCost + registryCost
  };
}